import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronRight } from "lucide-react";

// Fileira do topo do link (pontos, entrega grátis e cupons) como carrossel: pontinhos embaixo e, na borda, degradê com
// "›" quando tem cartão escondido ao lado (Pedro, 06/10/2026: "com indicador de carrossel e mostrando que tem mais coisas
// ao lado"). Rolagem livre, sem snap: o cupom tocado se centraliza sozinho (scrollIntoView do Storefront).
export function FaixaDeBeneficios({ children, className = "", testId }: { children: ReactNode; className?: string; testId?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [m, setM] = useState({ total: 0, ativo: 0, sobra: false, inicio: true, fim: true });

  const medir = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const itens = Array.from(el.children) as HTMLElement[];
    const sobra = el.scrollWidth > el.clientWidth + 4;
    const inicio = el.scrollLeft <= 4;
    const fim = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    let ativo = 0;
    let menor = Number.POSITIVE_INFINITY;
    itens.forEach((it, i) => {
      const d = Math.abs(it.offsetLeft - el.scrollLeft);
      if (d < menor) {
        menor = d;
        ativo = i;
      }
    });
    // No fim da rolagem o último cartão nem sempre encosta na esquerda: o pontinho dele acende mesmo assim.
    if (fim) ativo = itens.length - 1;
    setM((a) =>
      a.total === itens.length && a.ativo === ativo && a.sobra === sobra && a.inicio === inicio && a.fim === fim
        ? a
        : { total: itens.length, ativo, sobra, inicio, fim },
    );
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const agendar = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(medir);
    };
    medir();
    el.addEventListener("scroll", agendar, { passive: true });
    window.addEventListener("resize", agendar);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(agendar) : null;
    ro?.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", agendar);
      window.removeEventListener("resize", agendar);
      ro?.disconnect();
    };
  }, [medir]);
  // Cartões entram e saem com os dados (cupom, pontos): mede de novo a cada render; o setM acima não re-renderiza à toa.
  useEffect(() => {
    medir();
  });

  const irPara = (i: number) => {
    const el = ref.current;
    const it = el?.children[i] as HTMLElement | undefined;
    if (el && it) el.scrollTo({ left: it.offsetLeft, behavior: "smooth" });
  };
  const avancar = () => {
    const el = ref.current;
    if (el) el.scrollBy({ left: Math.max(120, el.clientWidth * 0.75), behavior: "smooth" });
  };

  return (
    <div className={`${className} ${m.sobra ? "" : "mb-3"}`}>
      <div className="relative">
        <div ref={ref} className="relative flex gap-2 overflow-x-auto hover-scrollbar pb-1" data-testid={testId}>
          {children}
        </div>
        {m.sobra && !m.inicio && (
          <div aria-hidden className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-card to-transparent" />
        )}
        {m.sobra && !m.fim && (
          <div className="pointer-events-none absolute inset-y-0 right-0 w-14 flex items-center justify-end bg-gradient-to-l from-card via-card/90 to-transparent">
            <button
              type="button"
              onClick={avancar}
              aria-label="Ver mais"
              className="pointer-events-auto mr-0.5 w-7 h-7 rounded-full bg-card border border-border shadow-sm flex items-center justify-center text-foreground/70"
              data-testid="faixa-mais"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
      {m.sobra && m.total > 1 && (
        <div className="flex justify-center mt-0.5" data-testid="faixa-indicador">
          {Array.from({ length: m.total }, (_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => irPara(i)}
              aria-label={`Ir para o item ${i + 1}`}
              aria-current={i === m.ativo}
              className="p-1"
            >
              <span className={`block h-1.5 rounded-full transition-all ${i === m.ativo ? "w-4 bg-primary" : "w-1.5 bg-muted-foreground/30"}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
